import {
    CloudDownload,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { LabeledValue, LabelIdValue } from '~/components/Fields/LabeledValue';
import RawSeriesProperties from '~/components/Header/BreadcrumbNavigation/ImageBreadcrumb/ImageDetails/RawSeriesProperties';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import {
    AvailableImage,
    SessionImageInfo,
} from '~/hooks/image/usePrimaryImage';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { formatSeriesDate } from '~/utils/date';
import ImageItem from './ImageItem';

function SessionImageProperties({
    modality,
    info,
}: {
    modality: string;
    info: SessionImageInfo;
}) {
    const intl = useIntl();
    const xResCm = (info.xResMm / 10).toFixed(2);
    const yResCm = (info.yResMm / 10).toFixed(2);
    const zResCm = (info.zResMm / 10).toFixed(2);
    const formattedDate = info.date ? formatSeriesDate(info.date) : undefined;

    return (
        <Box display="flex" flexDirection="column" gap={0.25}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <CloudDownload
                    sx={{
                        fontSize: 14,
                        color: 'primary.main',
                        flexShrink: 0,
                    }}
                />
                <Typography fontSize={11} color="primary.main" fontWeight={500}>
                    <FormattedMessage id="image.availableForDownload.tooltip" />
                </Typography>
            </Box>
            <LabeledValue
                label={intl.formatMessage({ id: 'modality' })}
                value={modality}
                inline
            />
            {formattedDate && (
                <LabeledValue
                    label={intl.formatMessage({ id: 'seriesDate' })}
                    value={formattedDate}
                    sensitive
                    inline
                />
            )}
            <LabeledValue
                label={intl.formatMessage({ id: 'matrix' })}
                value={`${info.xSize} x ${info.ySize} x ${info.zSize}`}
                inline
            />
            <LabeledValue
                label={
                    <>
                        <FormattedMessage id="resolution" /> (cm):
                    </>
                }
                value={`${xResCm} x ${yResCm} x ${zResCm}`}
                inline
            />
            <LabelIdValue
                label={intl.formatMessage({ id: 'frameOfReferenceUid' })}
                id={info.frameOfReferenceUid}
                inline
            />
        </Box>
    );
}

const tooltipSlotProps = {
    popper: {
        modifiers: [
            {
                name: 'offset' as const,
                options: { offset: [-9, 0] },
            },
        ],
    },
    tooltip: {
        sx: (theme: {
            palette: { divider: string; background: { paper: string } };
        }) => ({
            py: 2,
            px: 2,
            borderRadius: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderLeft: 'none',
            backgroundColor: theme.palette.background.paper,
        }),
    },
};

const modalityChipSx = {
    fontSize: 10,
    maxHeight: '12px',
    px: 0,
};

interface AvailableImageItemProps {
    image: AvailableImage;
    isSecondary?: boolean;
    hasSharedFrameOfReference?: boolean;
    isSelected?: boolean;
    onSelect?: () => void;
    onDownload?: () => void;
    onReformat?: () => void;
    onDelete?: () => void;
}

/**
 * Unified image list item that handles all AvailableImage states:
 * - Loaded Image3D → delegates to ImageItem
 * - Raw series (needs reformat) → simple row with warning indicator
 * - Session/remote (not loaded) → simple row with download action
 */
export default function AvailableImageItem({
    image,
    isSecondary,
    hasSharedFrameOfReference,
    isSelected,
    onSelect,
    onDownload,
    onReformat,
    onDelete,
}: AvailableImageItemProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    if (image.imageObject) {
        return (
            <ImageItem
                image={image.imageObject}
                isSecondary={isSecondary}
                hasSharedFrameOfReference={hasSharedFrameOfReference}
                isSelected={isSelected}
                onSelect={onSelect}
                onDelete={onDelete}
            />
        );
    }

    const needsReformat = !!image.rawSeries;
    const isSessionRemote = !needsReformat && !image.imageObject;
    const description =
        (needsReformat
            ? image.rawSeries!.seriesModule.seriesDescription
            : image.label) || intl.formatMessage({ id: 'unknown.series' });

    const sliceCount = needsReformat
        ? image.rawSeries!.numberOfObjects
        : image.sliceCount;

    const handleClick = needsReformat ? onReformat : onDownload;

    const spacingLabel = (() => {
        if (needsReformat) {
            const firstSlice = image.rawSeries!.dicomImageSlices[0];
            if (!firstSlice) {
                return undefined;
            }
            const spacingCm = Array.from(firstSlice.pixelSpacing)
                .map((s) => (s / 10).toFixed(2))
                .join(' x ');
            return intl.formatMessage(
                { id: 'image.spacingValue' },
                { spacing: spacingCm }
            );
        }
        if (isSessionRemote && image.sessionImageInfo) {
            const xCm = (image.sessionImageInfo.xResMm / 10).toFixed(2);
            const yCm = (image.sessionImageInfo.yResMm / 10).toFixed(2);
            return intl.formatMessage(
                { id: 'image.spacingValue' },
                { spacing: `${xCm} x ${yCm}` }
            );
        }
        return undefined;
    })();

    const seriesDate = (() => {
        if (needsReformat) {
            return image.rawSeries!.seriesModule.seriesDate;
        }
        if (isSessionRemote) {
            return image.sessionImageInfo?.date;
        }
        return undefined;
    })();

    const tooltipTitle = (() => {
        if (needsReformat) {
            return (
                <RawSeriesProperties
                    series={image.rawSeries!}
                    onDelete={onDelete}
                />
            );
        }
        if (isSessionRemote && image.sessionImageInfo) {
            return (
                <SessionImageProperties
                    modality={image.modality}
                    info={image.sessionImageInfo}
                />
            );
        }
        return '';
    })();

    return (
        <SelectableListItem isSelected={isSelected} onClick={handleClick}>
            <Tooltip
                title={tooltipTitle}
                placement="right-start"
                enterDelay={500}
                leaveDelay={200}
                slotProps={tooltipSlotProps}
            >
                <Box display="grid">
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        width="100%"
                        overflow="hidden"
                    >
                        {needsReformat ? (
                            <WarningAmberIcon
                                sx={{
                                    fontSize: 12,
                                    color: 'warning.main',
                                    flexShrink: 0,
                                }}
                            />
                        ) : (
                            <CloudDownload
                                sx={{
                                    fontSize: 12,
                                    color: 'grey.500',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Chip
                            label={image.modality}
                            size="small"
                            color={needsReformat ? 'warning' : 'default'}
                            sx={{
                                mr: -0.25,
                                ...modalityChipSx,
                            }}
                        />
                        <Typography
                            fontSize={12}
                            noWrap
                            flex={1}
                            minWidth={0}
                            title={showSensitiveInfo ? description : undefined}
                            color={
                                isSelected || isSelected === undefined
                                    ? 'text.primary'
                                    : 'text.secondary'
                            }
                        >
                            <SensitiveInfoTypography>
                                {description}
                            </SensitiveInfoTypography>
                        </Typography>
                        {sliceCount !== undefined && (
                            <Typography
                                fontSize={10}
                                color="text.secondary"
                                whiteSpace="nowrap"
                                flexShrink={0}
                            >
                                {intl.formatMessage(
                                    { id: 'image.sliceCount' },
                                    { count: sliceCount }
                                )}
                            </Typography>
                        )}
                    </Box>
                    {(spacingLabel || seriesDate) && (
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={0.5}
                            width="100%"
                            overflow="hidden"
                        >
                            {spacingLabel && (
                                <Typography
                                    fontSize={10}
                                    color="text.secondary"
                                    noWrap
                                >
                                    {spacingLabel}
                                </Typography>
                            )}
                            {seriesDate && (
                                <Typography
                                    fontSize={10}
                                    color="text.secondary"
                                    whiteSpace="nowrap"
                                    flexShrink={0}
                                >
                                    <SensitiveInfoTypography>
                                        {formatSeriesDate(seriesDate)}
                                    </SensitiveInfoTypography>
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Tooltip>
        </SelectableListItem>
    );
}
